import React, { useState, useRef } from 'react';
import {injectIntl, defineMessages} from "react-intl";
import {reisy, use} from 'reisy';
import { Cookies } from '../environment';

const messages = defineMessages({
	message: {
		id: "cookieconsent.message",
		defaultMessage: "Like many other websites we need cookies to render our services as well as to improve on them. <br>Which types of cookies would you accept?",
	},
	accept: {
		id: "cookieconsent.accept",
		defaultMessage: "OK",
	},
	accept_all: {
		id: "cookieconsent.acceptAll",
		defaultMessage: "accept all",
	},
	deny_all: {
		id: "cookieconsent.denyAll",
		defaultMessage: "deny all",
	},
	save: {
		id: "cookieconsent.save",
		defaultMessage: "save",
	},
	functional: {
		id: "cookieconsent.label.functional",
		defaultMessage: "functional",
	},
	statistics: {
		id: "cookieconsent.label.statistics",
		defaultMessage: "statistics",
	},
	marketing: {
		id: "cookieconsent.label.marketing",
		defaultMessage: "marketing",
	},
	details: {
		id: "cookieconsent.details",
		defaultMessage: "cookie details",
	},
	detailIntro: {
		id: "cookieconsent.details.intro",
		defaultMessage: "You can always change your choice of cookies above as well as remove the cookies stored on your computer via your webbrowser's settings.",
	},
	functionalDetails: {
		id: "cookieconsent.details.functional",
		defaultMessage: "These cookies are needed for this website to work correctly and securely. <em>sessionid, csrftoken, ps-cookie-settings, pagestrip-likes</em>",
	},
	statisticsDetails: {
		id: "cookieconsent.details.statistics",
		defaultMessage: "We use Google Analytics for our site statistics. For that to work, Google has to set three cookies <em>_ga, _gat, _gid</em> with a lifespan of up to two years.",
	},
	marketingDetails: {
		id: "cookieconsent.details.marketing",
		defaultMessage: "No marketing no cry.",
	}
});

const styles = reisy`
@namespace CookieConsent;
show: false;
options: functional, statistics, marketing;
cookieName: ps-cookie-settings;
cookieNameStatistics: ps_use_analytics;
protectedCookies: sessionid, csrftoken, pagestrip-likes; 
reload: false;
cookielifespan: year;
showDenyAll: false;

Wrapper {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 9999;
	background: rgba(0,0,0,0.5);
	opacity: 0;
	visibility: hidden;
	transition: all .3s ease-in-out;

	@media print {
		display: none;
	}
}

WrapperShow {
	opacity: 1;
	visibility: visible;
}

Box {
	position: fixed;
	left: 0;
	right: 0;
	bottom: 0;
	max-width: 500px;
	margin: 0 auto;
	background: #000;
	color: #fff;
	font-family: $Fonts.text;
	padding: 2rem;
	box-sizing: border-box;
	max-height: 100vh;
	overflow-x: hidden;
	overflow-y: auto;
	overflow-scrolling: touch;

	a {
		color: inherit;
	}
}

Message {

}

Options {
	list-style: none;
	display: flex;
	flex-direction: row;
	padding: 1rem 0;
	margin: 0;

	label {
		display: flex;
		margin-right: 1rem;
		position: relative;
		flex-direction: row;
		align-items: center;
	}

	input {
		opacity: 0;
		visibility: hidden;
		position: absolute;
		top: 0;
		left: 0;
	}

	span {
		padding-left: 1.5rem;
		position: relative;

		&::before {
			content: '';
			display: block;
			width: 1rem;
			height: 1rem;
			position: absolute;
			left: 0;
			top: 0;
			background: #666;
			border-radius: 3px;
			box-sizing: border-box;
		}

		&::after {
			content: '';
			visibility: hidden;
			opacity: 0;
			display: block;
			width: .5rem;
			height: .25rem;
			border-left: 2px solid #fff;
			border-bottom: 2px solid #fff;
			transform: rotate(-45deg);
			position: absolute;
			top: .25rem;
			left: .1875rem;
		}
	}

	input:checked + span {
		&::after {
			visibility: visible;
			opacity: 1;
		}
	}

	input:disabled + span {
		&::after,
		&::before {
			opacity: .5;
		}
	}
}

Buttons {
	display: flex;
	flex-direction: row;

	button {
		padding: .5rem 1rem;
		outline: 0;
		border: 0;
		border-radius: .5rem;
		font-size: 1rem;
		font-family: $Fonts.text;
		margin-right: .5rem;
		transition: all .2s ease-in-out;
	}
}

ButtonPrimary {
	color: #000;
	background: #fff;

	&:hover {
		transform: scale(1.1);
	}
}

ButtonSecondary {
	color: #000;
	background: #999;

	&:hover {
		background: #ccc;
	}
}

ButtonText {
	color: #fff;
	background:  transparent;
	opacity: .7;
	padding-left: 0 !important;
	padding-right: 0 !important;

	&:hover {
		opacity: 1;
	}
}

Details {

}

DetailIntro {
	margin: 1rem 0 0;
}

DetailGroup {
	margin: 1rem 0 0;

	strong {
		text-transform: capitalize;
	}
}

`;

const acceptAllCookies = () => {
	saveAcceptedCookies(styles.options);
}

const denyAllCookies = () => {
	saveAcceptedCookies('functional');
}

const acceptSelectedCookies = elem => {
	if (elem) {
		saveAcceptedCookies([].map.call(elem.querySelectorAll('input:checked'), i => i.value).join(','));
		return true;
	}
	return false;
}

const saveAcceptedCookies = accepted => {
	const currentConsent = currentCookieConsent();
	clearCookies();
	let expiryDate = new Date();
	if (styles.cookielifespan === 'session') {
		expiryDate = null;
	} else {
		expiryDate.setFullYear(expiryDate.getFullYear() + 1);
	} 
	Cookies.set(styles.cookieName, accepted, expiryDate ? expiryDate.toUTCString() : '');
	if (~accepted.indexOf('statistics')) {
		Cookies.set(styles.cookieNameStatistics, 'true', expiryDate ? expiryDate.toUTCString() : '');
	}
	if (styles.reload) {
		setTimeout(() => window.location.reload(), 500);
	} else {
		const event = document.createEvent('Event');
		
		event.initEvent('consentChanged', true, true);

		event.previousConsent = currentConsent
		event.currentConsent = accepted

		document.dispatchEvent(event);
	}
}

function clearCookies() {
	const prot = styles.protectedCookies.split(',').map(c => c.trim()).filter(Boolean);
	const cookies = document.cookie.split(';').map(c => c.trim()).filter(Boolean);

	cookies.forEach(c => {
		const [name, value] = c.split('=');
		if (!~prot.indexOf(name)) {
			Cookies.set(name, '', 'Thu, 01 Jan 1970 00:00:01 GMT');
			Cookies.set(name, '', 'Thu, 01 Jan 1970 00:00:01 GMT', '.pagestrip.com');
		}
	})
}

let setCookieOpen = null;
export function showCookieSettings() {
	if(setCookieOpen) {
		setCookieOpen(true);
	}
}

export function hasCookieConsent(type='functional') {
	return !!~(Cookies.get(styles.cookieName) || '').split(',').map(p => p.trim()).filter(Boolean).indexOf(type)
}

export function currentCookieConsent() {
	return (Cookies.get(styles.cookieName) || '').split(',').map(p => p.trim()).filter(Boolean)
}

if (IS_BROWSER) {
	window.psShowCookieSettings = showCookieSettings;
	window.psHasCookieConsent = hasCookieConsent;
}

const CookieConsent = ({intl: {formatMessage}}) => {
	if (!styles.show || !IS_BROWSER) { 
		//TODO: maybe send event and change current cookies to some default
		return null; 
	}

	const settings = currentCookieConsent();
	const list = useRef(null);
	const [open, setOpen] = useState(!settings.length);
	setCookieOpen = setOpen;
	const [showDetails, setShowDetails] = useState(false);

	React.useEffect(() => {
		const event = document.createEvent('Event');
		event.initEvent('consentInitialized', true, true);
		event.currentConsent = settings;
		document.dispatchEvent(event);
	}, []);

	if (!~settings.indexOf('functional')) {
		settings.unshift('functional');
	}

	const options = styles.options.split(',').map(o => o.trim()).filter(Boolean);

	return (
	<div {...use(styles.Wrapper, open ? styles.WrapperShow : null)}>
		<div {...use(styles.Box)}>
			<div {...use(styles.Message)} dangerouslySetInnerHTML={{__html: formatMessage(messages.message)}}></div>
			<ul {...use(styles.Options)} ref={list}>
				{options.map(o => (
					<li key={o}>
						<label>
							<input type="checkbox" name={`cookie_consent_${o}`} value={o} defaultChecked={~settings.indexOf(o)} disabled={o === 'functional'} autoComplete="off" /> 
							<span>{formatMessage(messages[o])}</span>
						</label>
					</li>
				))}
			</ul>
			<div {...use(styles.Buttons)}>
				<button {...use(styles.ButtonPrimary)} onClick={() => { acceptAllCookies(); setOpen(false); }}>{formatMessage(messages.accept_all)}</button> 
				{styles.showDenyAll ? <button {...use(styles.ButtonSecondary)} onClick={() => { denyAllCookies(); setOpen(false); }}>{formatMessage(messages.deny_all)}</button> : null}
				<button {...use(styles.ButtonSecondary)} onClick={() => setOpen(!acceptSelectedCookies(list.current))}>{formatMessage(messages.save)}</button> 
				<button {...use(styles.ButtonText)} onClick={() => setShowDetails(!showDetails)}>{formatMessage(messages.details)}</button> 
			</div>
			{showDetails ? <div {...use(styles.Details)}>
				<div {...use(styles.DetailIntro)}>
					{formatMessage(messages.detailIntro)}
				</div>
				{options.map(o => (
					<div key={o} {...use(styles.DetailGroup)}>
						<strong>{formatMessage(messages[o])}</strong>
						<div dangerouslySetInnerHTML={{__html: formatMessage(messages[o+'Details'])}}></div>
					</div>
				))}
			</div> : null}
		</div>
	</div>
)};

export default injectIntl(CookieConsent);



// WEBPACK FOOTER //
// ./lib/components/cookie-consent.js